import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import type { DebugParams } from '../types';

enum ExecutePlatformByUAOperation {
  CreateConnectionWithInput = 'createConnectionWithInput',
  FetchApplicationByName = 'fetchApplicationByName',
  FetchJavaArchivePodLogs = 'fetchJavaArchivePodLogs',
  FetchEntityPermissions = 'fetchEntityPermissions',
  FetchUserEntityPermissions = 'fetchUserEntityPermissions',
  SaveAndAuthorizeConnection = 'saveAndAuthorizeConnection',
  UpdateConnectionWithInput = 'updateConnectionWithInput',
  CreateAgentActivationCode = 'createAgentActivationCode',
  GetAgentConfig = 'getAgentConfig',
  DownloadAgentPackageFile = 'downloadAgentPackageFile',
  GetAgentSupportedOS = 'getAgentSupportedOS',
  GetAgentSupportedPackages = 'getAgentSupportedPackages',
  CreateApplicationSession = 'createApplicationSession',
}

type OperationFunction = (
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
) => Promise<unknown>;

type ModuleLoader = () => Promise<{ default: OperationFunction }>;

const PLATFORM_BY_UA_OPERATION_ID_VS_QUERY_FN: Record<ExecutePlatformByUAOperation, ModuleLoader> =
  {
    [ExecutePlatformByUAOperation.CreateConnectionWithInput]: () =>
      import('./operationQueryFn/createConnectionWithInput'),
    [ExecutePlatformByUAOperation.FetchApplicationByName]: () =>
      import('./operationQueryFn/fetchApplicationByName'),
    [ExecutePlatformByUAOperation.FetchJavaArchivePodLogs]: () =>
      import('./operationQueryFn/fetchJavaArchivePodLogs'),
    [ExecutePlatformByUAOperation.FetchEntityPermissions]: () =>
      import('./operationQueryFn/fetchEntityPermissions'),
    [ExecutePlatformByUAOperation.FetchUserEntityPermissions]: () =>
      import('./operationQueryFn/fetchUserEntityPermissions'),
    [ExecutePlatformByUAOperation.SaveAndAuthorizeConnection]: () =>
      import('./operationQueryFn/saveAndAuthorizeConnection'),
    [ExecutePlatformByUAOperation.UpdateConnectionWithInput]: () =>
      import('./operationQueryFn/updateConnectionWithInput'),
    [ExecutePlatformByUAOperation.CreateAgentActivationCode]: () =>
      import('./operationQueryFn/createAgentActivationCode'),
    [ExecutePlatformByUAOperation.GetAgentConfig]: () =>
      import('./operationQueryFn/getAgentConfig'),
    [ExecutePlatformByUAOperation.DownloadAgentPackageFile]: () =>
      import('./operationQueryFn/downloadAgentPackageFile'),
    [ExecutePlatformByUAOperation.GetAgentSupportedOS]: () =>
      import('./operationQueryFn/getAgentSupportedOS'),
    [ExecutePlatformByUAOperation.GetAgentSupportedPackages]: () =>
      import('./operationQueryFn/getAgentSupportedPackages'),
    [ExecutePlatformByUAOperation.CreateApplicationSession]: () =>
      import('./operationQueryFn/createApplicationSession'),
  };

export async function executePlatformByUAOperation(
  executeWorkflowNodeRequest: ExecuteWorkflowNodeRequest,
  debugParams: DebugParams,
): Promise<unknown> {
  const operationId = executeWorkflowNodeRequest.inputs?.operationId as
    | ExecutePlatformByUAOperation
    | undefined;

  if (!operationId) {
    console.error('Operation ID is not defined');
    return Promise.reject(new Error('Operation ID is not defined'));
  }

  const moduleLoader = PLATFORM_BY_UA_OPERATION_ID_VS_QUERY_FN[operationId];

  try {
    const { default: operationFunction } = await moduleLoader();
    return await operationFunction(executeWorkflowNodeRequest, debugParams);
  } catch (error) {
    console.error(`Error loading or executing operation ${operationId}:`, error);
    throw error;
  }
}
